import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "../../../../common/hooks/hooks";
import { openGlobalModal } from "../../../../common/features/globalModal/globalModalSlice";
import { useMutationWithFeedback } from "../../../../common/hooks/useMutationWithFeedback";
import { useProcessCheckoutMutation } from "../api/checkoutApi";
import {
  selectCustomerNote,
  selectAppointmentSelection,
} from "../checkoutSelectors";
import type { ProcessCheckoutPayload } from "../../../../types/api/wcStoreApi/wcStoreApiCheckoutEndpointsTypes";
import type { TransformedAddress } from "../../cart/types/cartApiTypes";
import type {
  TransformedCheckoutData,
  TransformedPaymentResult,
} from "../types/checkoutApiTypes";

// Helper to map TransformedAddress back to API Raw format (snake_case)
const mapAddressToApiFormat = (address: TransformedAddress | undefined) => {
  if (!address) return {} as any;
  return {
    first_name: address.firstName,
    last_name: address.lastName,
    company: address.company,
    address_1: address.address1,
    address_2: address.address2,
    city: address.city,
    state: address.state,
    postcode: address.postcode,
    country: address.country || "", // Don't force TR
    email: address.email,
    phone: address.phone,
  };
};

/**
 * Handles payment method specific logic for headless flows (e.g. Iyzico/PayTR Modal).
 * Returns `true` if the payment was handled (e.g. modal opened), preventing default redirect.
 */
const handleHeadlessPayment = (
  paymentMethodId: string,
  paymentResult: TransformedPaymentResult,
  dispatch: any,
  t: TFunction,
): boolean => {
  const gatewayIds = window.commonVariables?.paymentGatewayIds;

  if (!gatewayIds) return false;

  // --- IYZICO ---
  if (paymentMethodId === gatewayIds.IYZICO) {
    const iyzicoHtmlB64 =
      paymentResult?.paymentDetails?.iyzico_html_content_b64;

    if (iyzicoHtmlB64) {
      try {
        const iyzicoHtml = atob(iyzicoHtmlB64);
        dispatch(
          openGlobalModal({
            contentKey: "paymentForm",
            title: t("checkout.paymentModal.title", "Secure Payment"),
            contentProps: {
              iyzicoHtmlContent: iyzicoHtml,
              containerId: "iyzipay-checkout-form",
            },
          }),
        );
        return true;
      } catch (e) {
        console.error("Failed to decode Iyzico content", e);
      }
    }
  }

  // --- PAYTR ---
  if (paymentMethodId === gatewayIds.PAYTR) {
    const paytrToken = paymentResult?.paymentDetails?.paytr_token;

    if (paytrToken) {
      dispatch(
        openGlobalModal({
          contentKey: "paymentForm",
          title: t("checkout.paymentModal.title", "Secure Payment"),
          contentProps: {
            paytrToken: paytrToken,
            containerId: "paytr-checkout-form",
          },
        }),
      );
      return true;
    }
  }

  return false;
};

/**
 * A composite hook that orchestrates the entire order submission process.
 */
export const useCheckoutSubmit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customerNote = useAppSelector(selectCustomerNote);
  // NEW: Grab the appointment selection
  const appointmentSelection = useAppSelector(selectAppointmentSelection);

  const [processCheckout, mutationResult] = useProcessCheckoutMutation();

  const { trigger: triggerPlaceOrder, isLoading: isProcessing } =
    useMutationWithFeedback<any, ProcessCheckoutPayload>({
      mutation: [processCheckout, mutationResult],
      loadingMessage: t(
        "feedback.checkout.processing",
        "Processing your order...",
      ),
      successMessage: null,
      errorMessages: {
        default: t(
          "feedback.checkout.error",
          "There was an error processing your order.",
        ),
        // --- APPOINTMENT RACE CONDITION HANDLING ---
        // This maps the specific error code from the backend to a localized message
        woocommerce_rest_appointment_unavailable: t(
          "checkoutPage.appointment.unavailableError",
          "Üzgünüz, seçtiğiniz randevu saati ödeme işlemi sırasında başkası tarafından alındı. Lütfen farklı bir saat seçiniz.",
        ),
      },
    });

  const submitOrder = useCallback(
    async (
      checkoutData: TransformedCheckoutData,
      selectedPaymentMethod: string,
    ) => {
      // 1. Prepare Addresses
      const billingAddr = mapAddressToApiFormat(checkoutData.billingAddress);
      const shippingAddr = mapAddressToApiFormat(checkoutData.shippingAddress);

      // 2. Prepare Extensions
      const extensions: any = {
        additional_billing_info: {
          customer_type: checkoutData.billingAddress?.customerType,
          tckn: checkoutData.billingAddress?.tckn,
          vkn: checkoutData.billingAddress?.vkn,
        },
        legal_agreements: {
          agreed: true,
        },
      };

      // 3. Append Appointment Data (Fixing the boolean validation error)
      if (checkoutData.appointmentData?.requiresAppointment) {
        extensions.appointment_data = {
          requires_appointment: true, // EXPLICITLY passing the boolean to satisfy WP REST Schema
          date: appointmentSelection.date,
          time: appointmentSelection.time,
        };
      }

      // 4. Trigger Mutation
      const result = await triggerPlaceOrder({
        payment_method: selectedPaymentMethod,
        billing_address: billingAddr,
        shipping_address: shippingAddr,
        customer_note: customerNote,
        extensions: extensions,
      });

      // 5. Handle Success
      if (result.success && result.data) {
        const { paymentResult, orderId } = result.data;

        if (paymentResult?.paymentDetails?.gateway_init_error) {
          const errorMessage =
            paymentResult.paymentDetails.messages ||
            t(
              "feedback.checkout.gatewayInitError",
              "Payment method unavailable. Please try another.",
            );
          toast.error(errorMessage, { duration: 5000 });
          return;
        }

        if (checkoutData.billingAddress?.email) {
          sessionStorage.setItem(
            "wc_customer_billing_email",
            checkoutData.billingAddress.email,
          );
        }

        if (orderId) {
          sessionStorage.setItem("latest_order_id", String(orderId));
        }

        const handled = handleHeadlessPayment(
          selectedPaymentMethod,
          paymentResult,
          dispatch,
          t,
        );

        if (handled) {
          return;
        }

        toast.success(
          t("feedback.checkout.success", "Order placed successfully!"),
        );

        if (paymentResult?.redirectUrl) {
          window.location.href = paymentResult.redirectUrl;
        }
      }
    },
    [triggerPlaceOrder, dispatch, t, customerNote, appointmentSelection],
  );

  return { submitOrder, isProcessing };
};
